// SUB-OFFICE 7 — renderer, camera rig, the two real lights, fog, and the
// global dim uniform. The camera is never player-rotatable: fov 22, elevation
// 52 deg, yaw -15 deg, distance 28m, critically damped spring follow with
// velocity look-ahead (capped 1.4m). Ceremonies override via a cue stack.
//
// Exactly two lights. No shadow maps, ever. Dimming is a uniform injected
// into every material via onBeforeCompile — with customProgramCacheKey set
// and ONE shared uniforms object, so the injection survives program caching.

import {
  DirectionalLight, FogExp2, HemisphereLight, PerspectiveCamera, Scene,
  Vector3, WebGLRenderer, Color, NeutralToneMapping, PCFSoftShadowMap,
  type Material, type WebGLProgramParametersWithUniforms,
} from 'three';
import { PAL } from '../palette';
import type { PerfTier } from '../../shared/types';
import type { FirstPersonView } from './fpview';

export type ViewMode = 'TOP' | 'FP';
const FP_FOV = 70;
const FP_NEAR = 0.12;

// ---------------------------------------------------------------------------
// The shared uniforms object (ONE instance; every patched material points here)
// ---------------------------------------------------------------------------

export const bureauUniforms = {
  uDim: { value: 1.0 },
  uTime: { value: 0.0 },
  // GRAPHICS R2 (#75) — soft toon strength (0 = smooth Lambert, 1 = full band).
  // Live-tunable via window.__bureau for A/B; shipped default is the tuned value.
  uToon: { value: 0.9 },
};

interface PatchOpts {
  dimExempt?: boolean; // the Machine's own voice stays lit during ceremonies
  banded?: boolean; // photocopier banding via vertex-color alpha flag
}

/**
 * Inject the global dim (and optional world-space banding) into a material.
 * customProgramCacheKey guarantees three.js compiles distinct programs for
 * patched vs unpatched materials and never serves a stale cached program.
 */
export function patchMaterial(mat: Material, opts: PatchOpts = {}): void {
  if (opts.dimExempt) return; // exempt materials are simply not patched
  const banded = !!opts.banded;
  mat.customProgramCacheKey = () => `bureau-dim-band${banded ? 1 : 0}`;
  mat.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    shader.uniforms.uDim = bureauUniforms.uDim;
    shader.uniforms.uTime = bureauUniforms.uTime;
    shader.uniforms.uToon = bureauUniforms.uToon;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vBureauWorld;')
      .replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\nvBureauWorld = (modelMatrix * vec4(position, 1.0)).xyz;'
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        '#include <common>\nuniform float uDim;\nuniform float uTime;\nuniform float uToon;\nvarying vec3 vBureauWorld;\nfloat bureauBand = 0.0;'
      )
      // GRAPHICS R2 (#75) — SOFT TOON RAMP. Quantize only the DIRECT key-light
      // diffuse into ~3 bands with smooth-stepped plateau edges (a soft cel, not
      // a hard one). Ambient/hemi fill stays continuous so shadowed planes keep
      // their gradient — the banding reads as a stylized key wrap, the signature
      // clean-stylized look, at zero extra passes. Only lit materials carry
      // <lights_fragment_end>; Basic emissives never match, so they skip it.
      .replace(
        '#include <lights_fragment_end>',
        '#include <lights_fragment_end>\n' +
          '{\n' +
          '  vec3 _dd = reflectedLight.directDiffuse;\n' +
          '  float _tl = max(_dd.r, max(_dd.g, _dd.b));\n' +
          '  if (uToon > 0.0 && _tl > 0.0008) {\n' +
          '    float _n = 3.0;\n' +
          '    float _b = floor(_tl * _n) / _n;\n' +
          '    float _f = fract(_tl * _n);\n' +
          '    float _q = _b + smoothstep(0.30, 0.70, _f) / _n;\n' +
          '    reflectedLight.directDiffuse = _dd * mix(1.0, _q / _tl, uToon);\n' +
          '  }\n' +
          '}'
      )
      .replace(
        '#include <color_fragment>',
        '#include <color_fragment>\n#ifdef USE_COLOR_ALPHA\n  bureauBand = vColor.a;\n  diffuseColor.a = 1.0;\n#endif'
      )
      .replace(
        '#include <opaque_fragment>',
        (banded
          ? 'float bStripe = step(0.5, fract(vBureauWorld.y * 3.0 + vBureauWorld.x * 0.02));\n' +
            'outgoingLight *= 1.0 - 0.02 * bStripe * bureauBand;\n'
          : '') +
          'outgoingLight *= uDim;\n#include <opaque_fragment>'
      );
  };
}

// ---------------------------------------------------------------------------
// Critically damped smoothing (no per-frame allocation)
// ---------------------------------------------------------------------------

class SmoothDamp3 {
  cur = new Vector3();
  vel = new Vector3();

  /** smoothTime ~0.13 => ~0.4s visual settle. */
  step(target: Vector3, smoothTime: number, dt: number): Vector3 {
    const omega = 2 / Math.max(0.0001, smoothTime);
    const x = omega * dt;
    const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
    for (const axis of ['x', 'y', 'z'] as const) {
      const change = this.cur[axis] - target[axis];
      const temp = (this.vel[axis] + omega * change) * dt;
      this.vel[axis] = (this.vel[axis] - omega * temp) * exp;
      this.cur[axis] = target[axis] + (change + temp) * exp;
    }
    return this.cur;
  }
}

// ---------------------------------------------------------------------------

export type CameraCue = (dt: number, camera: PerspectiveCamera) => boolean; // false => done, popped

const ELEVATION = (52 * Math.PI) / 180;
const YAW = (-15 * Math.PI) / 180;

export class SceneManager {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  tier: PerfTier = 'A';
  /** The ALTERNATE VANTAGE: TOP is the long lens; FP is the eye. */
  view: ViewMode = 'TOP';
  /** Assigned by boot; owns pointer lock, mouse-look, and head-bob. */
  fp: FirstPersonView | null = null;
  /** The one shadow-casting key light (GRAPHICS R1); null until constructed. */
  private key: DirectionalLight | null = null;

  private smooth = new SmoothDamp3();
  private lookTarget = new Vector3();
  private offset = new Vector3();
  private tmpTarget = new Vector3();
  private distance = 28;
  private baseFov = 22;
  private cueStack: CameraCue[] = [];
  private dimTarget = 1;
  private dimRate = 4; // per second

  // rolling frame governor
  private frameAcc: number[] = [];
  private cleanTime = 0;
  private steppedUp = false;
  onTierChange: ((tier: PerfTier) => void) | null = null;

  constructor(container: HTMLElement) {
    this.renderer = new WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setClearColor(new Color(PAL.fog));
    // GRAPHICS R1 (#75) — real tonemapping. Khronos PBR-Neutral rolls off the
    // key-light highlights without desaturating the palette (unlike ACES, which
    // would grey the whole Bureau); mid-tones pass near-linear so the authored
    // hexes survive. The post pass reads a half-float RT and does its own final
    // lin->srgb, so the RT stores tonemapped-linear here: correct, not doubled.
    this.renderer.toneMapping = NeutralToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    container.appendChild(this.renderer.domElement);

    this.scene = new Scene();
    // fog swallows corridor ends so the Bureau implies infinity; the density is
    // retuned per vantage in setView (0.016 long-lens, 0.020 eye — the FP body
    // is deeper in the building's air). AFTER-HOURS keeps its own hex elsewhere.
    this.scene.fog = new FogExp2(PAL.fog, 0.016);
    this.scene.background = new Color(PAL.fog);

    this.camera = new PerspectiveCamera(22, 1, 0.5, 120);

    // exactly two lights — GRAPHICS R1 (#75): rebalanced from flat-fill-dominant
    // (hemi 0.85 / key 0.55) to real directional FORM. The flat-shaded Lambert
    // boxes carry true face normals, so a dominant key now shades the planes
    // that face it and lets the others fall into the room's fill — the single
    // cheapest kill for the "everything is one flat brightness" CG read. Tone
    // mapping above tames the raised key so nothing clips to white.
    const hemi = new HemisphereLight(PAL.hemiSky, PAL.hemiGround, 0.58);
    this.scene.add(hemi);
    const key = new DirectionalLight(PAL.keyLight, 1.05);
    key.position.set(12, Math.tan((35 * Math.PI) / 180) * 20, 10); // 35 deg key
    // GRAPHICS R1 (#75) — the key now casts a soft PCF shadow. A single ortho
    // frustum (+-40 around origin) blankets the whole resident interior at
    // ~0.04m/texel; bias/normalBias tuned against the flat-shaded slabs to kill
    // acne without peter-panning. This is the grounding move: furniture and the
    // bodies drop real contact shadows onto the floor instead of floating on
    // painted discs. Cost is gated in applyShadowTier (off on C, half-res on B).
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.bias = -0.0004;
    key.shadow.normalBias = 0.03;
    const sc = key.shadow.camera;
    sc.left = -40; sc.right = 40; sc.top = 40; sc.bottom = -40;
    sc.near = 0.5; sc.far = 80;
    sc.updateProjectionMatrix();
    this.scene.add(key);
    this.scene.add(key.target); // target rests at origin
    this.key = key;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    this.applyShadowTier();

    this.applyViewport();
    window.addEventListener('resize', () => this.applyViewport());
  }

  private applyViewport(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const portrait = h > w;
    this.baseFov = portrait ? 26 : 22;
    this.distance = portrait ? 28 * 1.15 : 28;
    this.camera.fov = this.baseFov;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    const dprCap = this.tier === 'A' ? 2 : this.tier === 'B' ? 1.5 : 1;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, dprCap));
    this.renderer.setSize(w, h);
    const horiz = Math.cos(ELEVATION) * this.distance;
    this.offset.set(Math.sin(YAW) * horiz, Math.sin(ELEVATION) * this.distance, Math.cos(YAW) * horiz);
  }

  /** Switch vantage. The Bureau sees no difference. */
  setView(view: ViewMode): void {
    if (view === this.view) return;
    this.view = view;
    // retune fog density on the vantage switch: the eye sees more air than the
    // long lens, so the FP haze is thicker and the corridors dissolve sooner.
    const fog = this.scene.fog;
    if (fog instanceof FogExp2) fog.density = view === 'FP' ? 0.020 : 0.016;
    if (view === 'FP') {
      this.camera.near = FP_NEAR;
    } else {
      this.camera.near = 0.5;
      this.camera.rotation.set(0, 0, 0);
      this.camera.rotation.order = 'XYZ';
    }
    this.camera.updateProjectionMatrix();
    if (this.fp) this.fp.setActive(view === 'FP');
  }

  setTier(tier: PerfTier): void {
    if (tier === this.tier) return;
    this.tier = tier;
    this.applyViewport();
    this.applyShadowTier();
    if (this.onTierChange) this.onTierChange(tier);
  }

  /** Gate the shadow cost by tier: full 2048 map on A, 1024 on B, OFF on C.
   *  Disposing the old shadow map forces three to reallocate at the new size. */
  private applyShadowTier(): void {
    const on = this.tier !== 'C';
    this.renderer.shadowMap.enabled = on;
    if (!this.key) return;
    this.key.castShadow = on;
    const size = this.tier === 'A' ? 2048 : 1024;
    if (this.key.shadow.mapSize.x !== size) {
      this.key.shadow.mapSize.set(size, size);
      if (this.key.shadow.map) {
        this.key.shadow.map.dispose();
        this.key.shadow.map = null as unknown as typeof this.key.shadow.map;
      }
    }
  }

  /** Ceremony cue-stack override point: top cue owns the camera until done. */
  pushCue(cue: CameraCue): void {
    this.cueStack.push(cue);
  }
  clearCues(): void {
    this.cueStack.length = 0;
  }

  /** Global dim (0.5 = adjudication hall dim; 1 = business as usual). */
  setDim(v: number, rate = 4): void {
    this.dimTarget = v;
    this.dimRate = rate;
  }
  /** Verdict release: light snaps back — the Bureau's applause (200ms). */
  snapDim(): void {
    this.dimTarget = 1;
    this.dimRate = 12;
  }

  /** Follow the avatar. vel is world-units/s; look-ahead capped at 1.4m. */
  follow(target: Vector3, vel: Vector3, dt: number): void {
    this.tmpTarget.copy(vel).multiplyScalar(0.5);
    const l = this.tmpTarget.length();
    if (l > 1.4) this.tmpTarget.multiplyScalar(1.4 / l);
    this.tmpTarget.add(target);
    this.tmpTarget.y = 0.9;
    const smoothed = this.smooth.step(this.tmpTarget, 0.13, Math.min(dt, 0.05));
    this.lookTarget.copy(smoothed);

    if (this.cueStack.length > 0) {
      const cue = this.cueStack[this.cueStack.length - 1];
      if (!cue(dt, this.camera)) this.cueStack.pop();
    } else if (this.view === 'FP' && this.fp) {
      this.fp.apply(this.camera, target, vel, dt);
      if (this.camera.fov !== FP_FOV) {
        this.camera.fov = FP_FOV;
        this.camera.updateProjectionMatrix();
      }
    } else {
      this.camera.position.set(
        smoothed.x + this.offset.x,
        this.offset.y,
        smoothed.z + this.offset.z
      );
      this.camera.lookAt(this.lookTarget);
      if (this.camera.fov !== this.baseFov) {
        this.camera.fov = this.baseFov;
        this.camera.updateProjectionMatrix();
      }
    }
  }

  /** Per-frame housekeeping: dim easing, clock, governor sampling. */
  update(dt: number, frameMs: number): void {
    bureauUniforms.uTime.value += dt;
    const d = bureauUniforms.uDim.value;
    if (d !== this.dimTarget) {
      const step = this.dimRate * dt;
      bureauUniforms.uDim.value =
        Math.abs(this.dimTarget - d) <= step ? this.dimTarget : d + Math.sign(this.dimTarget - d) * step;
    }

    // tier governor: 30-frame rolling average; >20ms steps DOWN;
    // 30s of clean (<12ms) frames steps UP exactly once per session.
    this.frameAcc.push(frameMs);
    if (this.frameAcc.length > 30) this.frameAcc.shift();
    if (this.frameAcc.length === 30) {
      const avg = this.frameAcc.reduce((a, b) => a + b, 0) / 30;
      if (avg > 20 && this.tier !== 'C') {
        this.setTier(this.tier === 'A' ? 'B' : 'C');
        this.frameAcc.length = 0;
        this.cleanTime = 0;
      } else if (avg < 12) {
        this.cleanTime += dt;
        if (this.cleanTime > 30 && !this.steppedUp && this.tier !== 'A') {
          this.steppedUp = true;
          this.setTier(this.tier === 'C' ? 'B' : 'A');
          this.frameAcc.length = 0;
        }
      } else {
        this.cleanTime = 0;
      }
    }
  }

  /** Debug (backtick): draw calls / tris / programs. */
  info(): { calls: number; triangles: number; programs: number } {
    const i = this.renderer.info;
    return {
      calls: i.render.calls,
      triangles: i.render.triangles,
      programs: i.programs ? i.programs.length : 0,
    };
  }
}

/** BOOT tier guess before the warmed probe: coarse pointers start at B. */
export function guessTier(): PerfTier {
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  return coarse ? 'B' : 'A';
}
